import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { CheckCircle2, Circle, AlertTriangle } from 'lucide-react'

export interface SetupGuideStep {
  title: string
  description: string
}

export interface SetupGuidePrerequisite {
  label: string
  met: boolean
  link: string
  linkLabel: string
}

export interface SetupGuideProps {
  icon: ReactNode
  title: string
  description: string
  steps: SetupGuideStep[]
  action?: ReactNode
  prerequisites?: SetupGuidePrerequisite[]
  prerequisitesTitle?: string
}

export function SetupGuide({
  icon,
  title,
  description,
  steps,
  action,
  prerequisites,
  prerequisitesTitle,
}: SetupGuideProps) {
  const allPrereqsMet = !prerequisites || prerequisites.every((p) => p.met)

  return (
    <div className="flex flex-col items-center py-12 px-6">
      {/* Icon hidden on mobile (matches EmptyState, EP-90 T1) — the guideline
          copy + steps carry the message on small screens without the large icon. */}
      <div className="hidden sm:block text-base-content/30 mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-base-content/60 mb-8 max-w-lg text-center">{description}</p>

      {prerequisites && (
        <div
          className={`w-full max-w-lg mb-6 rounded-lg border p-4 ${
            allPrereqsMet ? 'border-success/30 bg-success/5' : 'border-warning/30 bg-warning/5'
          }`}
        >
          <div className="flex items-center gap-2 mb-3 font-medium text-sm">
            {allPrereqsMet ? (
              <CheckCircle2 className="w-4 h-4 text-success" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-warning" />
            )}
            <span>{prerequisitesTitle}</span>
          </div>
          <div className="space-y-2">
            {prerequisites.map((prereq, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {prereq.met ? (
                    <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-base-content/30 shrink-0" />
                  )}
                  <span className={prereq.met ? 'text-base-content/60' : ''}>{prereq.label}</span>
                </div>
                {!prereq.met && (
                  <Link to={prereq.link} className="text-primary hover:underline text-xs font-medium">
                    {prereq.linkLabel}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="w-full max-w-lg rounded-lg border border-base-200 bg-base-100 divide-y divide-base-200">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-4 p-4">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-semibold shrink-0 mt-0.5">
              {i + 1}
            </div>
            <div>
              <h4 className="font-medium text-sm">{step.title}</h4>
              <p className="text-sm text-base-content/60 mt-0.5">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {action && <div className="mt-6">{action}</div>}
    </div>
  )
}
